import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Home } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { renderLinkedText, resolveImage } from "@/lib/content";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { PageSeo } from "@/components/PageSeo";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function ServicesPage() {
  const { content } = useContent();
  const s = content.services_page;
  const pillars = [
    { ...s.pillar1, icon: HeartHandshake, image: content.services.care.image, alt: content.services.care.image_alt || "NRI parent care in Mohali — a caregiver's reassuring visit" },
    { ...s.pillar2, icon: Home, image: content.services.property.image, alt: content.services.property.image_alt || "NRI property management in Chandigarh — a cared-for family home" },
  ];

  return (
    <div className="grain bg-[#FAF8F5]" data-testid="services-page">
      <PageSeo path="/services" />
      <Navbar />
      <main>
        <section className="bg-[#1C1917] pb-20 pt-40 lg:pt-48">
          <div className="mx-auto max-w-5xl px-6 lg:px-10">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#FAF8F5]/25 bg-black/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FAF8F5]/90 backdrop-blur-md"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#C5A059]" />
              {s.kicker}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#FAF8F5] sm:text-5xl"
              data-testid="services-page-h1"
            >
              {renderLinkedText(s.h1)}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-6 max-w-2xl text-base leading-relaxed text-[#FAF8F5]/80 md:text-lg"
            >
              {renderLinkedText(s.intro)}
            </motion.p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <div className="grid gap-8 lg:grid-cols-2">
            {pillars.map((p, i) => (
              <motion.article
                key={p.title}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.12 }}
                className="group relative overflow-hidden rounded-3xl shadow-[0_30px_60px_rgba(28,25,23,0.12)]"
                data-testid={`services-pillar-${i}`}
              >
                <img
                  src={resolveImage(p.image)}
                  alt={p.alt}
                  className="hero-image-grade h-[340px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#1C1917]/60 [mask-image:linear-gradient(to_top,black_35%,transparent)]" />
                <div className="absolute bottom-0 p-8 lg:p-10">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#7A2B21] text-[#FAF8F5]">
                    <p.icon size={22} />
                  </div>
                  <h2 className="font-display text-3xl font-medium tracking-tight text-[#FAF8F5]">
                    {renderLinkedText(p.title)}
                  </h2>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-[#FAF8F5]/85">
                    {renderLinkedText(p.body)}
                  </p>
                  <Link
                    to={p.cta_href}
                    data-testid={`services-pillar-cta-${i}`}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-6 py-3 text-xs font-bold uppercase tracking-wider text-[#1C1917] transition-[background-color,transform] duration-300 hover:bg-[#FAF8F5] hover:scale-[1.03]"
                  >
                    {p.cta_label} <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
